import React, { useState, useRef, useEffect } from "react";
import MyNavbar from '../../../components/general/navbar/MyNavbar';
import Footer from '../../../components/general/footer/Footer';
import BookingPage from "../../doctor/docbuttons/BookingPage";
import DoctorProfile from "../../doctor/docbuttons/DoctorProfile";
import api from "../../../api/axiosInstance";
import "../../../assets/css/doctor/docbuttons/DoctorPage.css";

// Backend (/doctors) field names → component field names
const mapDoctor = (d) => ({
  id: d.id,
  name: d.name,
  specialty: d.specialization || "General",
  degree: d.education || "",
  experience: d.experience ? `${d.experience} years` : "—",
  rating: d.rating || 0,
  reviews: d.reviews || 0,
  location: d.location || "",
  image: d.profile_image_url || "https://ui-avatars.com/api/?name=" + encodeURIComponent(d.name || "Doctor") + "&size=400&background=00005a&color=fff&bold=true",
  available: d.available || "Not Available",
  fee: d.fee || 0,
});

const getAIResponse = async (message, doctorsList, muslimMode) => {
  try {
    const res = await api.post("/chat", { message, muslim_mode: muslimMode });

    if (!res.data.success) {
      return { text: "Sorry, something went wrong. Please try again.", doctors: [], specialty: null };
    }

    const { reply, specialty, wants_recommendation, calming_exercise, ayat_arabic, ayat_reference, ayat_meaning } = res.data;

    let doctors = [];
    if (wants_recommendation) {
      doctors = specialty
        ? doctorsList.filter(d => d.specialty === specialty).slice(0, 3)
        : doctorsList.filter(d => d.available === "Available Today").slice(0, 3);
    }

    return {
      text: reply,
      doctors,
      specialty,
      calmingExercise: calming_exercise,
      ayatArabic: ayat_arabic,
      ayatReference: ayat_reference,
      ayatMeaning: ayat_meaning,
    };
  } catch (err) {
    console.error("AI chat request failed:", err);

    if (err.response?.status === 401) {
      return { text: "Please log in to use the AI chat assistant.", doctors: [], specialty: null };
    }

    return { text: "Connection error. Please try again.", doctors: [], specialty: null };
  }
};

const DoctorsPage = () => {
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);
  const [doctorsError, setDoctorsError] = useState(null);

  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [muslimMode, setMuslimMode] = useState(false);
  const [filters, setFilters] = useState({ specialty: "", location: "", experience: "", rating: "", availability: "", priceRange: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [messages, setMessages] = useState([
    { type: "ai", text: "Hello! I'm your AI mental health assistant. How can I help you today? You can describe your symptoms or type 'recommend a therapist' to get personalized suggestions.", doctors: [], specialty: null, ayat: null }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const chatMessagesRef = useRef(null);
  const chatSectionRef = useRef(null);
  const cardsRef = useRef([]);

  const quickPrompts = ["I'm feeling anxious", "Help with stress", "Dealing with depression", "Speech problems", "Recommend a therapist"];

  // Fetch approved doctors from backend
  useEffect(() => {
    let cancelled = false;

    const fetchDoctors = async () => {
      setDoctorsLoading(true);
      setDoctorsError(null);
      try {
        const res = await api.get("/doctors");
        if (!cancelled) {
          setDoctors(res.data.map(mapDoctor));
        }
      } catch (err) {
        console.error("Failed to load doctors:", err);
        if (!cancelled) {
          setDoctorsError("Could not load therapists. Please try again later.");
        }
      } finally {
        if (!cancelled) setDoctorsLoading(false);
      }
    };

    fetchDoctors();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    if (showAIChat && chatSectionRef.current) {
      setTimeout(() => {
        chatSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [showAIChat]);

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

    const handleBook = (doctor) => {
    setSelectedDoctor(doctor);
    setShowBooking(true);
    setShowProfile(false);
  };

  const handleProfile = (doctor) => {
    setSelectedDoctor(doctor);
    setShowProfile(true);
    setShowBooking(false);
  };

  const handleClose = () => {
    setShowBooking(false);
    setShowProfile(false);
    setSelectedDoctor(null);
  };

  useEffect(() => {
    if (!showBooking && !showProfile) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [showBooking, showProfile]);
  const handleFilterChange = (name, value) => setFilters(prev => ({ ...prev, [name]: value }));

  const clearFilters = () => {
    setFilters({ specialty: "", location: "", experience: "", rating: "", availability: "", priceRange: "" });
    setSearchQuery("");
  };

  const handleSendMessage = async (overrideMessage) => {
    const msgToSend = overrideMessage || inputMessage;
    if (!msgToSend.trim()) return;
    const userMsg = { type: "user", text: msgToSend, doctors: [], specialty: null };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage("");
    setIsTyping(true);

    const response = await getAIResponse(msgToSend, doctors, muslimMode);
    setMessages(prev => [...prev, { type: "ai", ...response }]);
    setIsTyping(false);
  };

  const filteredDoctors = doctors.filter(doctor => {
    const matchesSearch = doctor.name?.toLowerCase().includes(searchQuery.toLowerCase()) || doctor.specialty?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSpecialty = !filters.specialty || doctor.specialty === filters.specialty;
    const matchesLocation = !filters.location || doctor.location === filters.location;
    const expYears = parseInt(doctor.experience);
    const matchesExperience = !filters.experience || (filters.experience === "5-10" && expYears >= 5 && expYears <= 10) || (filters.experience === "10-15" && expYears >= 10 && expYears <= 15) || (filters.experience === "15+" && expYears >= 15);
    const matchesRating = !filters.rating || doctor.rating >= parseFloat(filters.rating);
    const matchesAvailability = !filters.availability || doctor.available.includes(filters.availability);
    const matchesPrice = !filters.priceRange || (filters.priceRange === "0-3000" && doctor.fee <= 3000) || (filters.priceRange === "3000-5000" && doctor.fee > 3000 && doctor.fee <= 5000) || (filters.priceRange === "5000+" && doctor.fee > 5000);
    return matchesSearch && matchesSpecialty && matchesLocation && matchesExperience && matchesRating && matchesAvailability && matchesPrice;
  });

  const specialties = [...new Set(doctors.map(d => d.specialty).filter(Boolean))];
  const locations   = [...new Set(doctors.map(d => d.location).filter(Boolean))];

  return (
    <div className="thp-wrapper">

      {/* Font Awesome for refined chat icons (robot/user/moon/send) */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />

      <MyNavbar />

      {/* ===== HERO ===== */}
      <section className="thp-hero">
        <div className="thp-hero-bg">
          <div className="thp-hero-shape thp-hero-shape-1"></div>
          <div className="thp-hero-shape thp-hero-shape-2"></div>
          <div className="thp-hero-shape thp-hero-shape-3"></div>
        </div>
        <div className="thp-hero-inner">
          <div className="thp-hero-badge">
            <span className="thp-hero-dot"></span>
            AI-Powered Mental Health Support
          </div>
          <h1 className="thp-hero-title">
            Find Your Perfect<br />
            <span className="thp-hero-accent">Mental Health Specialist</span>
          </h1>
          <p className="thp-hero-sub">
            Chat with our AI assistant for instant guidance or book directly with certified therapists, psychiatrists & specialists
          </p>
          <div className="thp-hero-features">
            <div className="thp-hero-feat"><span className="thp-feat-icon">🤖</span><span>24/7 AI Support</span></div>
            <div className="thp-hero-feat"><span className="thp-feat-icon">🔒</span><span>Private & Secure</span></div>
            <div className="thp-hero-feat"><span className="thp-feat-icon">⚡</span><span>Instant Booking</span></div>
            <div className="thp-hero-feat"><span className="thp-feat-icon">⭐</span><span>Verified Doctors</span></div>
          </div>
          <button className="thp-hero-chat-btn" onClick={() => setShowAIChat(!showAIChat)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            {showAIChat ? "Close AI Chat" : "Start AI Chat Support"}
          </button>
        </div>
      </section>

      {/* ===== AI CHAT ===== */}
      {showAIChat && (
        <section className="thp-chat-section" ref={chatSectionRef}>
          <div className="thp-chat-box">

            {/* Chat Header */}
            <div className="thp-chat-head">
              <div className="thp-chat-status">
                <span className="thp-status-avatar-ring">
                  <i className="fa-solid fa-robot"></i>
                  <span className="thp-status-dot"></span>
                </span>
                <div>
                  <div className="thp-status-name">AI Health Assistant</div>
                  <div className="thp-status-sub">Online — Replies instantly</div>
                </div>
              </div>

              <div className="thp-chat-head-actions">
                {/* Muslim Mode Toggle */}
                <div className="thp-muslim-toggle-wrap">
                  <i className="fa-solid fa-moon thp-muslim-icon"></i>
                  <span className="thp-muslim-label">Muslim Mode</span>
                  <div
                    className={`thp-toggle-track ${muslimMode ? "thp-toggle-active" : ""}`}
                    onClick={() => setMuslimMode(prev => !prev)}
                  >
                    <div className="thp-toggle-thumb" />
                  </div>
                </div>

                <button className="thp-chat-close" onClick={() => setShowAIChat(false)}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="thp-chat-messages" ref={chatMessagesRef}>
              {messages.map((msg, idx) => (
                <div key={idx}>
                  <div className={`thp-msg ${msg.type === "ai" ? "thp-msg-ai" : "thp-msg-user"}`}>
                    {msg.type === "ai" && (
                      <div className="thp-msg-avatar thp-avatar-ai">
                        <i className="fa-solid fa-robot"></i>
                      </div>
                    )}
                    <div className="thp-msg-bubble" dangerouslySetInnerHTML={{
                      __html: msg.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    }} />
                    {msg.type === "user" && (
                      <div className="thp-msg-avatar thp-avatar-user">
                        <i className="fa-solid fa-user"></i>
                      </div>
                    )}
                  </div>

                  {/* Doctor recommendation cards */}
                  {msg.doctors && msg.doctors.length > 0 && (
                    <div className="thp-chat-rec-cards">
                      {msg.doctors.map(doc => (
                        <div key={doc.id} className="thp-chat-rec-card">
                          <div className="thp-chat-rec-img">
                            <img src={doc.image} alt={doc.name} />
                            <span className={`thp-rec-avail ${doc.available.includes("Today") ? "thp-avail-today" : "thp-avail-tomorrow"}`}>
                              {doc.available.includes("Today") ? "Today" : "Tomorrow"}
                            </span>
                          </div>
                          <div className="thp-chat-rec-info">
                            <div className="thp-rec-name">{doc.name}</div>
                            <div className="thp-rec-spec">{doc.specialty}</div>
                            <div className="thp-rec-meta">
                              <span><i className="fa-solid fa-star"></i> {doc.rating} ({doc.reviews})</span>
                              <span><i className="fa-solid fa-location-dot"></i> {doc.location}</span>
                            </div>
                            <div className="thp-rec-fee">Rs. {Number(doc.fee).toLocaleString()} / session</div>
                            <div className="thp-rec-actions">
                              <button className="thp-rec-book-btn" onClick={() => handleBook(doc)}>
                                <i className="fa-solid fa-calendar-check"></i> Book Now
                              </button>
                              <button className="thp-rec-profile-btn" onClick={() => handleProfile(doc)}>View Profile</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {muslimMode && msg.type === "ai" && (msg.calmingExercise || msg.ayatArabic) && (
                    <div className="thp-wazaif-block">
                      <div className="thp-wazaif-title">
                        <i className="fa-solid fa-mosque"></i> Quranic Healing Recommendation
                      </div>
                      {msg.calmingExercise && (
                        <div className="thp-wazaif-row">
                          <strong>Calming Exercise:</strong> {msg.calmingExercise}
                        </div>
                      )}
                      {msg.ayatArabic && (
                        <div className="thp-wazaif-row thp-ayat-row">
                          <p
                            style={{
                              fontWeight: "bold",
                              direction: "rtl",
                              textAlign: "right",
                              fontSize: "1.2em",
                              fontFamily: "'Amiri', 'Scheherazade New', serif",
                              margin: "6px 0 4px",
                            }}
                          >
                            {msg.ayatArabic}
                          </p>
                          {msg.ayatReference && <div><em>{msg.ayatReference}</em></div>}
                          {msg.ayatMeaning && <div>{msg.ayatMeaning}</div>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="thp-msg thp-msg-ai">
                  <div className="thp-msg-avatar thp-avatar-ai">
                    <i className="fa-solid fa-robot"></i>
                  </div>
                  <div className="thp-typing"><span></span><span></span><span></span></div>
                </div>
              )}
            </div>

            {/* Quick Prompts */}
            <div className="thp-quick-prompts">
              {quickPrompts.map((p, i) => (
                <button key={i} className="thp-quick-btn" onClick={() => handleSendMessage(p)}>{p}</button>
              ))}
            </div>

            {/* Chat Input */}
            <div className="thp-chat-input-row">
              <input
                type="text"
                placeholder="Describe your symptoms or ask for a recommendation..."
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyPress={e => e.key === "Enter" && handleSendMessage()}
                className="thp-chat-input"
              />
              <button onClick={() => handleSendMessage()} className="thp-send-btn">
                <i className="fa-solid fa-paper-plane"></i>
              </button>
            </div>

          </div>
        </section>
      )}

      {/* ===== FILTERS BAR ===== */}
      <section className="thp-filters-bar">
        <div className="thp-filters-container">
          <div className="thp-filters-top">
            <h3 className="thp-filters-title">Filter Therapists</h3>
            <button onClick={clearFilters} className="thp-clear-btn">Clear All</button>
          </div>
          <div className="thp-filters-grid">
            {[
              { label: "Specialty", key: "specialty", options: specialties, placeholder: "All Specialties" },
              { label: "Location",  key: "location",  options: locations,   placeholder: "All Locations" },
            ].map(f => (
              <div key={f.key} className="thp-filter-item">
                <label>{f.label}</label>
                <select value={filters[f.key]} onChange={e => handleFilterChange(f.key, e.target.value)}>
                  <option value="">{f.placeholder}</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div className="thp-filter-item">
              <label>Experience</label>
              <select value={filters.experience} onChange={e => handleFilterChange("experience", e.target.value)}>
                <option value="">Any Experience</option>
                <option value="5-10">5–10 years</option>
                <option value="10-15">10–15 years</option>
                <option value="15+">15+ years</option>
              </select>
            </div>
            <div className="thp-filter-item">
              <label>Min Rating</label>
              <select value={filters.rating} onChange={e => handleFilterChange("rating", e.target.value)}>
                <option value="">Any Rating</option>
                <option value="4.5">4.5+ Stars</option>
                <option value="4.7">4.7+ Stars</option>
                <option value="4.9">4.9+ Stars</option>
              </select>
            </div>
            <div className="thp-filter-item">
              <label>Availability</label>
              <select value={filters.availability} onChange={e => handleFilterChange("availability", e.target.value)}>
                <option value="">Any Time</option>
                <option value="Today">Today</option>
                <option value="Tomorrow">Tomorrow</option>
              </select>
            </div>
            <div className="thp-filter-item">
              <label>Fee Range</label>
              <select value={filters.priceRange} onChange={e => handleFilterChange("priceRange", e.target.value)}>
                <option value="">Any Price</option>
                <option value="0-3000">Rs. 0 – 3,000</option>
                <option value="3000-5000">Rs. 3,000 – 5,000</option>
                <option value="5000+">Rs. 5,000+</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* ===== DOCTORS GRID ===== */}
      <section className="thp-main-section">
        <div className="thp-container">
          <div className="thp-results-header">
            <h2>Showing <span className="thp-count">{filteredDoctors.length}</span> Therapist{filteredDoctors.length !== 1 ? "s" : ""}</h2>
          </div>

          {doctorsLoading ? (
            <div className="thp-no-results">
              <div className="thp-no-icon">⏳</div>
              <h3>Loading therapists...</h3>
            </div>
          ) : doctorsError ? (
            <div className="thp-no-results">
              <div className="thp-no-icon">⚠️</div>
              <h3>Something went wrong</h3>
              <p>{doctorsError}</p>
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="thp-no-results">
              <div className="thp-no-icon">🔍</div>
              <h3>No therapists found</h3>
              <p>Try adjusting your filters or search criteria</p>
              <button onClick={clearFilters} className="thp-reset-btn">Reset Filters</button>
            </div>
          ) : (
            <div className="thp-doctors-grid">
              {filteredDoctors.map((doctor, i) => (
                <div key={doctor.id} className="thp-doctor-card" ref={el => cardsRef.current[i] = el}>
                  <div className="thp-doc-img-wrap">
                    <img src={doctor.image} alt={doctor.name} className="thp-doc-img" />
                    <span className="thp-verified-badge">✓ Verified</span>
                    <span className={`thp-avail-badge ${doctor.available.includes("Today") ? "thp-avail-today" : "thp-avail-tomorrow"}`}>
                      {doctor.available}
                    </span>
                  </div>
                  <div className="thp-doc-body">
                    <h3 className="thp-doc-name">{doctor.name}</h3>
                    <p className="thp-doc-spec">{doctor.specialty}</p>
                    <p className="thp-doc-degree">{doctor.degree}</p>
                    <div className="thp-doc-meta">
                      <span>👤 {doctor.experience}</span>
                      <span>⭐ {doctor.rating} ({doctor.reviews})</span>
                    </div>
                    <div className="thp-doc-location">📍 {doctor.location}</div>
                    <div className="thp-doc-fee">
                      <span>Consultation Fee</span>
                      <span className="thp-fee-amt">Rs. {Number(doctor.fee).toLocaleString()}</span>
                    </div>
                    <div className="thp-doc-actions">
                      <button className="thp-btn-book" onClick={() => handleBook(doctor)}>Book Now</button>
                      <button className="thp-btn-profile" onClick={() => handleProfile(doctor)}>View Profile</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      {showBooking && selectedDoctor && <BookingPage doctor={selectedDoctor} onClose={handleClose} />}
      {showProfile && selectedDoctor && <DoctorProfile doctor={selectedDoctor} onClose={handleClose} onBookAppointment={() => handleBook(selectedDoctor)} />}
    </div>
  );
};

export default DoctorsPage;